/*
 * AboutUs page
 *
 * Displays information about the gym including:
 * - Coaches list (fetched from /coaches/)
 * - Facility details and an embedded FacilityGallery
 *
 * Behavior:
 * - On mount, fetches coaches from the backend and renders a grid of
 *   'CoachCard' components. The backend is expected to return an array of
 *   coach objects.
 * - Supports scrolling to anchors via the URL hash (e.g. /about#our-coaches)..
 * - This file is presentation-only: all networking is local fetch calls and
 *   no global state is mutated.
 */

import { useState, useEffect } from 'react';
import {
  Container, Typography, Grid, Box, Skeleton
} from '@mui/material';
import SportsMartialArtsIcon from '@mui/icons-material/SportsMartialArts';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SectionCard from '../components/SectionCard'; 
import FacilityGallery from '../components/FacilityGallery'; 
import CoachCard from '../components/CoachCard'; 
import ScrollSection from '../components/ScrollSection';
import { useLocation } from 'react-router-dom';

import api from '../api';
import { Helmet } from 'react-helmet';

function About() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 500);
      return () => clearTimeout(t);
    }
  }, [location]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/coaches/')
      .then((res) => {
        const data = res && res.data ? res.data : [];
        console.log(data);
        setCoaches(data);
        setLoading(false);
      })
      .catch(() => {
        setCoaches([]);
        setLoading(false);
      });
    }, []);

  return (
    <Container maxWidth="xl" sx={{ backgroundColor: '#111', color: '#fff', py: 6 }}>
      <Helmet>
        <title>About Kingdom MMA — Coaches & Facility in Edmonton</title>
        <meta name="description" content="Meet our experienced coaches and tour our 5,500 sq ft training facility in Edmonton. Classes for beginners to competitive athletes — learn more and join." />
        <link rel="canonical" href="https://www.kingdommixedmartialarts.ca/about" />
        <meta property="og:title" content="About — Kingdom MMA and Fitness" />
        <meta property="og:description" content="Meet the coaches and see our facility in Edmonton." />
        <meta property="og:url" content="https://www.kingdommixedmartialarts.ca/about" />
  <meta property="og:image" content="https://www.kingdommixedmartialarts.ca/logo-no-text.svg" />
      </Helmet>
      {/* Page title */}
      <Typography variant="h3" align="left" sx={{ mb: 5, fontWeight: 'bold' }}>
        About Us
      </Typography>

      {/* Section: Coaches */}
      <ScrollSection id="our-coaches">
        <SectionCard title="Our Coaches" icon={<SportsMartialArtsIcon />} invert={false}>
          <Typography sx={{ mb: 3 }}>
            Whether you're just getting started or have years of experience, our coaches have the expertise to take your skills to the next level. In addition to being
            skilled fighters, our coaches break things down clearly and help you make real progress through technical, detail-oriented instruction.
          </Typography>
          <Grid container spacing={4}>
            {loading ? (
              // Show skeleton loaders while loading to prevent layout shift
              Array.from({ length: 4 }).map((_, idx) => (
                <Grid item xs={12} sm={6} md={4} lg={4} xl={3} key={idx} sx={{ display: 'flex' }}>
                  <Box sx={{ width: '100%', backgroundColor: '#222', borderRadius: 2, p: 2 }}>
                    <Skeleton 
                      variant="rectangular" 
                      width="100%" 
                      height={350} 
                      sx={{ bgcolor: '#333', borderRadius: 1, mb: 2 }} 
                    />
                    <Skeleton variant="text" width="60%" sx={{ bgcolor: '#333', fontSize: '1.5rem' }} />
                    <Skeleton variant="text" width="40%" sx={{ bgcolor: '#333', mb: 1 }} />
                    <Skeleton variant="text" width="100%" sx={{ bgcolor: '#333' }} />
                    <Skeleton variant="text" width="100%" sx={{ bgcolor: '#333' }} />
                    <Skeleton variant="text" width="80%" sx={{ bgcolor: '#333' }} />
                  </Box>
                </Grid>
              ))
            ) : (
              coaches.map((coach, idx) => (
                <Grid item xs={12} sm={6} md={4} lg={4} xl={3} key={idx} sx={{ display: 'flex'}}>
                  <CoachCard coach={coach} delay={idx * 0.1} />
                </Grid>
              ))
            )}
          </Grid>
        </SectionCard>
      </ScrollSection>

      {/* Section: Facility */}
      <ScrollSection id="our-facility" >
        <SectionCard title="Our Facility" icon={<FitnessCenterIcon />} >
        
          <Typography sx={{ mb: 2 }}>
            Our 5,500 square foot facility is designed to support high-level training, with all the equipment and space you need for an effective and well-rounded training experience. Our members can enjoy:
          </Typography>
          <Box sx={{ pl: 2, mb: 2 }}>
            {[
              'Spacious, fully matted training zones for striking and grappling',
              'Heavy bags & striking pad equipment',
              'Boxing ring and MMA cage',
              'Strength and conditioning area',
              'Three washrooms, including one with a shower',
              'Four separate change stalls',
              'Small store with clothing & training equipment',
            ].map((item, i) => (
              <Typography key={i}>• {item}</Typography>
            ))}
          </Box>
          <Typography sx={{ mb: 2 }}>
            Facility is disinfected twice daily to ensure a clean training environment!
          </Typography>
          {/* FacilityGallery displays images of the gym */}
          <FacilityGallery />
        </SectionCard>
      </ScrollSection>
    </Container>
  );
}

export default About;
